import { useState } from "react";
import MonthlyTableRow, { RangeFilter } from "./MonthlyTableRow";
import {
  BaseRow,
  ClassRow,
  ProfitAndLossMonthlyData,
} from "@/types/profit-and-loss-monthly-api";
import i18n from "@/i18n";
import { useTranslation } from "react-i18next";
import { getAbbreviatedMonthNames } from "@/lib/time_utils";
import { H4 } from "@/components/ui/Typography";

interface MonthlyTableProps {
  monthlyData: ProfitAndLossMonthlyData;
  clientId: string;
  connectionId: string;
  dateRange: RangeFilter;
}

const transformRow = (row: BaseRow | ClassRow, locale: string): any => {
  const node: any = {
    name: row.title?.[locale] || "Unnamed",
    monthly: row.total || [],
    previous: row.totalPreviousPeriod || [],
    difference: row.difference || [],
    differencePercentage: row.differencePercentage || [],
    totalCost: row.total.reduce((a, b) => a + b, 0),
    key: row.key,
  };

  if ((row as ClassRow).subRows?.length) {
    node.children = (row as ClassRow).subRows
      .slice()
      .sort((a, b) => a.index - b.index)
      .map((r) => transformRow(r, locale));
  }

  return node;
};

const MonthlyTable = ({
  monthlyData,
  clientId,
  connectionId,
  dateRange,
}: MonthlyTableProps) => {
  const { t } = useTranslation("profit_and_loss");
  const [expanded, setExpanded] = useState<Record<string | number, boolean>>(
    {}
  );
  const locale = i18n.language;

  const toggle = (key: string | number) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const months = getAbbreviatedMonthNames(monthlyData.months);

  const allRows: (BaseRow | ClassRow)[] = [
    ...Object.values(monthlyData.classRows),
    monthlyData.grossProfit,
    monthlyData.ebit,
    monthlyData.profitBeforeTax,
  ];

  const sortedRows = allRows
    .slice()
    .sort((a, b) => a.index - b.index)
    .map((row) => transformRow(row, locale));

  const renderRow = (
    node: any,
    level = 0,
    keyPath: (string | number)[] = []
  ) => (
    <MonthlyTableRow
      key={keyPath.join("-")}
      node={node}
      level={level}
      keyPath={keyPath}
      expanded={expanded}
      toggle={toggle}
      renderRow={renderRow}
      clientId={clientId}
      connectionId={connectionId}
      dateRange={dateRange}
    />
  );
  return (
    <div className="overflow-auto">
      <table className="min-w-full border-collapse">
        <thead className="top-0 z-10 bg-white border-b">
          <tr>
            <H4 className="text-left px-4 py-3">{t("account")}</H4>
            {months.map((m, i) => (
              <H4 className="text-right px-2 py-3" as="th">
                {" "}
                {m}{" "}
              </H4>
            ))}
            <H4 className="text-right px-2 py-3">{t("total")}</H4>
          </tr>
        </thead>
        <tbody className="bg-white">
          {sortedRows.map((row, i) => renderRow(row, 0, ["sorted", i]))}
        </tbody>
      </table>
    </div>
  );
};

export default MonthlyTable;
